import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DefaultButton, DirectionalHint, IColumn, IPanelProps, IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton, Separator,
    Stack
} from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import InformationCallout from "components/controls/InformationCallout";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { appStyles, createNonStandard, createNonStandardColumns, EViewType, fadeIn, panelButtonsStyles, reconcileTiers } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, timestamp } from "global/Utility/TSFunctions";
import {
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import { IPanelEvent, IValueChoiceGroupOption } from "model/Extension";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IFlexibleFieldValue as FAM_IFlexibleFieldValue,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    IObjectWithNameId as FAM_IObjectWithNameId,
    IStopLoss as FAM_IStopLoss
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { Constants } from "../../global/Application/Constants";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import SnapshotLookup from "./Controls/SnapshotLookup";
import NonStandardEditor, { EVariant } from "./NonStandardEditor";
import { $att_$non_standard_$bcbs_benefitdefinition_id } from "model/ConfigAttributeConstants";

interface IConfirm {
}


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    benefit?: FAM_IBenefit
    groupConfiguration?: FAM_IGroupConfigurationData;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    stopLoss?: FAM_IStopLoss;
    section: IXrmTypes_bcbs_config_section;

    nonStandard?: FAM_INonStandard

    onAdd?: () => void

    onAddTemplate?: () => void;

    onCreateNsAndTemplate?: () => void;

    onCreateNsAndLinkTemplate?: () => void;
    onMovetemplateToThisSection?:() => void;
}

const enum EView {
    View_Source,
    View_Copy,
    View_Import,
    View_Add,
    View_CreateTemplate,
    View_AddAndTemplate,
    View_AddAndLinkTemplate,
    View_MoveTemplateHere
};

enum ENonstandardScope {
    GroupConfiguration,
    Benefit,
    AccountsReceivable,
    FeesAndPremiums,
    StopLoss
}

interface IScopeSearchResult extends FAM_IObjectWithNameId {
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
    accountsReceivable: FAM_IAccountsReceivable | null;
    feesAndPremium: FAM_IFeesAndPremiums | null;
    stopLoss: FAM_IStopLoss | null;
}
interface INonstandardSearchResult extends FAM_IObjectWithNameId {
    item: IBenefitNonstandardScope | IGroupConfigurationNonstandardScope | IAccountsReceivableNonstandardScope | IFeesAndPremiumsNonstandardScope | IStopLossNonstandardScope;
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IStopLoss;
    type: ENonstandardScope;
}

interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM_IBenefit
    type: ENonstandardScope.Benefit;
}
interface IGroupConfigurationNonstandardScope extends INonstandardScope {
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardScope.GroupConfiguration;
}
interface IAccountsReceivableNonstandardScope extends INonstandardScope {
    parent: FAM_IAccountsReceivable;
    type: ENonstandardScope.AccountsReceivable;
}
interface IFeesAndPremiumsNonstandardScope extends INonstandardScope {
    parent: FAM_IFeesAndPremiums;
    type: ENonstandardScope.FeesAndPremiums;
}
interface IStopLossNonstandardScope extends INonstandardScope {
    parent: FAM_IStopLoss;
    type: ENonstandardScope.StopLoss;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardWizard = (props: IProps) => {
    // current view state/stages
    const [view, setView] = useState<EView | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [optionSource, setOptionSource] = useState<IValueChoiceGroupOption<EView> | null>(null);

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: null,
        json: null,
    });

    const [nonStandard, setNonStandard] = useState<FAM_INonStandard | null>(props.nonStandard ?? null);

    const application = useApplication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);


    const disableAddingNonstandardByChangeType =
        (props.benefit && !application.ChangeTypePermissions.benefit.add_nonstandard) ||
        (props.groupConfiguration && !application.ChangeTypePermissions.canAddGroupConfigurationNonstandard);
    

    const optionsSource: IValueChoiceGroupOption<EView>[] = [
        {
            key: 'add',
            text: 'Add New',
            data: EView.View_Add,
            disabled: props.state.status.viewType === EViewType.ViewOnly || disableAddingNonstandardByChangeType
        },
        {
            key: 'import',
            text: 'Copy/Clone - From External Configuration/BlueRelationship',
            data: EView.View_Import,
            disabled: props.state.status.viewType === EViewType.ViewOnly || disableAddingNonstandardByChangeType
        },
        {
            key: 'template',
            text: 'Open Template Wizard',
            data: EView.View_CreateTemplate,
            disabled: nonStandard?.partOfTemplate === true || nonStandard === null || disableAddingNonstandardByChangeType
        },
        {
            key: 'nsandtemplate',
            text: 'New Templated Non-Standard',
            data: EView.View_AddAndTemplate,
            disabled: nonStandard !== null || disableAddingNonstandardByChangeType//disabled if non standard is is selected
        },
        {
            key: 'nsandLinktemplate',
            text: 'Create Non-Standard From Template',
            data: EView.View_AddAndLinkTemplate,
            disabled: nonStandard !== null || disableAddingNonstandardByChangeType
        },
        {
            key: 'nsTemplateMove',
            text: 'Move Templated Non-Standards To This Section',
            data: EView.View_MoveTemplateHere,
            disabled: nonStandard !== null || disableAddingNonstandardByChangeType
        }
    ];

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.isOpen) {
            setView(EView.View_Source);
            setOptionSource(null);
            setSourceContext(q => {
                return {
                    json: null,
                    nonStandardSearchResult: null,
                    scopeSearchResult: null,
                    snapshot: null
                }
            });

            if (props.nonStandard) {
                setNonStandard(n => {

                    let value = structuredClone(props.nonStandard);

                    return value ?? null
                })
            }

        }
    }, [props.isOpen, props.nonStandard]);

    function onRenderEntry(): JSX.Element {
        return <div className={fadeIn}>
            <ChoiceGroup
                options={optionsSource}
                label="Choose Source"
                required={true}
                selectedKey={optionSource?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionSource(option ?? null);
                }}
            />
        </div >;
    }

    function onRenderEntryFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionSource === null}
                onClick={async () => {

                    const view = optionSource?.data;

                    if (view === EView.View_Add) {

                        props.onAdd?.();
                        props.onDismiss?.();

                    }
                    else if (view === EView.View_CreateTemplate)
                    {
                        props.onAddTemplate?.();
                        props.onDismiss?.();
                    }
                    else if (view === EView.View_AddAndTemplate) {
                        props.onCreateNsAndTemplate?.();
                        props.onDismiss?.();

                    }
                    else if (view === EView.View_AddAndLinkTemplate) {
                        props.onCreateNsAndLinkTemplate?.();
                        props.onDismiss?.();
                    }
                    else if (view === EView.View_MoveTemplateHere) {
                        props.onMovetemplateToThisSection?.();
                        props.onDismiss?.();
                    }
                    else if (view === EView.View_Copy ) {

                        setSourceContext({
                            snapshot: props.state.data.snapshot,
                            json: props.state.data.json,
                            scopeSearchResult: null,
                        });
                        setView(optionSource?.data ?? null);
                    }
                    else {
                        setView(optionSource?.data ?? null);
                    }
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => props.onCancel?.()}
                styles={panelButtonsStyles}
            />
        </div>
    }

    function onRenderNsAndTemplateFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionSource === null}
                onClick={async () => {

                    const view = optionSource?.data;

                    if (view === EView.View_Add) {

                        props.onAdd?.();
                        props.onDismiss?.();

                    }
                    else if (view === EView.View_CreateTemplate) {
                        props.onAddTemplate?.();
                        props.onDismiss?.();
                    }
                    else if (view === EView.View_Copy) {

                        setSourceContext({
                            snapshot: props.state.data.snapshot,
                            json: props.state.data.json,
                            scopeSearchResult: null,
                        });
                        setView(optionSource?.data ?? null);
                    }
                    else {
                        setView(optionSource?.data ?? null);
                    }
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => props.onCancel?.()}
                styles={panelButtonsStyles}
            />
        </div>
    }

    function onRenderAdd(): JSX.Element {
        return onRenderUnknown();
    }

    function onRenderAddFooter(): JSX.Element {
        return onRenderUnknown();
    }

    /**
     * Search for all available non-standards inside a snapshot and output list of scopes
     * Group Configuration, Benefit 1, Benefit 2, ...
     * @param value 
     * @returns 
     */
    const onSearchScopes: TSearchPredicateAsync<IScopeSearchResult> = async (value) => {
        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<IScopeSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));

            if ((json?.nonStandards?.length ?? 0) > 0) {
                let add = false;

                const search = 'group configuration'.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    items.push({
                        key: '-1',
                        _index: -1,
                        scope: 'Group Configuration',
                        name: 'Group Configuration',
                        search: 'group configuration',
                        total: json?.nonStandards?.length ?? 0,
                        data: {
                            id: json!.id,
                            name: json!.name,
                            groupConfiguration: json ?? null,
                            benefit: null,
                            accountsReceivable: null,
                            feesAndPremium: null,
                            stopLoss: null
                        }
                    });
            }

            json?.benefits?.forEach((benefit, index) => {

                let add = false;
                const scope = `Benefits (${benefit.bcbs_name})/${benefit.name}`;
                const search = scope.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    if (benefit?.nonStandards?.length > 0)
                        items.push({
                            key: `benefit-${index}`,
                            _index: index,
                            scope: scope,
                            search: search,
                            name: scope,
                            total: benefit?.nonStandards?.length ?? 0,
                            data: {
                                id: benefit.id,
                                name: benefit.name,
                                groupConfiguration: null,
                                benefit: benefit,
                                accountsReceivable: null,
                                feesAndPremium: null,
                                stopLoss: null
                            }
                        });
            });

            // Accounts Receivable
            json?.claimsARs?.forEach((ar, index) => {

                let add = false;
                const scope = `Accounts Receivable/${ar.name}`;
                const search = scope.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    if ((ar?.nonStandards?.length ?? 0) > 0)
                        items.push({
                            key: `ar-${index}`,
                            _index: index,
                            scope: scope,
                            search: search,
                            name: scope,
                            total: ar?.nonStandards?.length ?? 0,
                            data: {
                                id: ar.id,
                                name: ar.name,
                                groupConfiguration: null,
                                benefit: null,
                                accountsReceivable: ar,
                                feesAndPremium: null,
                                stopLoss: null
                            }
                        });
            });

            // Fees and Premiums
            json?.feesAndPremiums?.forEach((fp, index) => {

                let add = false;
                const scope = `Fees and Premiums/${fp.name}`;
                const search = scope.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    if ((fp?.nonStandards?.length ?? 0) > 0)
                        items.push({
                            key: `fp-${index}`,
                            _index: index,
                            scope: scope,
                            search: search,
                            name: scope,
                            total: fp?.nonStandards?.length ?? 0,
                            data: {
                                id: fp.id,
                                name: fp.name,
                                groupConfiguration: null,
                                benefit: null,
                                accountsReceivable: null,
                                feesAndPremium: fp,
                                stopLoss: null
                            }
                        });
            });

            // Stop Loss
            json?.stopLoss?.forEach((sl, index) => {

                let add = false;
                const scope = `Stop Loss/${sl.name}`;
                const search = scope.toLowerCase();

                if (!searchValue)
                    add = true;

                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    if ((sl?.nonStandards?.length ?? 0) > 0)
                        items.push({
                            key: `sl-${index}`,
                            _index: index,
                            scope: scope,
                            search: search,
                            name: scope,
                            total: sl?.nonStandards?.length ?? 0,
                            data: {
                                id: sl.id,
                                name: sl.name,
                                groupConfiguration: null,
                                benefit: null,
                                accountsReceivable: null,
                                feesAndPremium: null,
                                stopLoss: sl
                            }
                        });
            });

            resolve({
                columns: [{
                    key: 'scope',
                    minWidth: 550,
                    name: 'Entity',
                    fieldName: 'scope',
                    isResizable: true,
                },
                {
                    key: 'total',
                    minWidth: 50,
                    name: 'Count',
                    fieldName: 'total',
                    isResizable: true,
                }],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,
            });
        });
    }

    const categoryNonStandard = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard'];
    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardSearchResult>) => {

        if (!auditData?.data?.item.nonStandard)
            return <></>;

        const nonStandard = auditData?.data?.item.nonStandard;

        const benefit = auditData?.data?.item.type === ENonstandardScope.Benefit
            ? auditData?.data?.item.parent as FAM_IBenefit
            : undefined;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    defaultValue={nonStandard ?? null}
                    benefit={benefit}
                    disabled={true}
                />

            </div>
        </InformationCallout>;
    }

    /**
     * Search for all records applicable to selected scope
     * @param value 
     * @returns 
     */
    const onSearchNonStandards: TSearchPredicateAsync<INonstandardSearchResult> = async (value) => {
        const searchValue = value?.toLowerCase() ?? '';

        return new Promise((resolve, reject) => {

            const items: IGridItem<INonstandardSearchResult>[] = [];

            sourceContext?.nonStandardSearchResult?.forEach((nonStandardItem, ix) => {

                let add = false;

                if (!add && !searchValue)
                    add = true;

                if (!add && nonStandardItem.nonStandard.name.toLowerCase().indexOf(searchValue) > -1)
                    add = true;

                if (add) {

                    const item: IGridItem<INonstandardSearchResult> = {
                        key: ix,
                        _index: ix,
                        name: nonStandardItem.nonStandard.name,
                        scope: nonStandardItem.parent.name,
                        data: {
                            name: nonStandardItem.nonStandard.name,
                            id: nonStandardItem.nonStandard.id,
                            item: nonStandardItem as IBenefitNonstandardScope | IGroupConfigurationNonstandardScope,
                        }
                    };


                    nonStandardItem.nonStandard.fields.forEach(eaField => {
                        const configAttributeId = eaField.configRef.id;
                        eaField.values.forEach(eachFieldValue => {
                            item[`${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`] = eachFieldValue.value;
                        });
                    });

                    items.push(item);

                }
            })

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardColumns(
                props.state.data.config,
                {
                    section: props.section,
                    benefit: props.benefit,
                    tierNames: null
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === $att_$non_standard_$bcbs_benefitdefinition_id)
                        return true;

                    return false;
                }
            );


            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    {
                        key: 'scope',
                        minWidth: 300,
                        name: 'Entity',
                        fieldName: 'scope',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }

    function onRenderCopy(): JSX.Element {

        return <div className={fadeIn}>
            <Stack tokens={{ childrenGap: 10 }}>

                {view === EView.View_Import && sourceContext?.snapshot === null &&
                    <SnapshotLookup
                        onPick={async (value) => {
                            if (value?.id) {
                                try {
                                    setSourceContext(q => { return { ...q, searching: true } });

                                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value.id);
                                    const result = await dataSource.downloadGroupConfigurationSnapshot(snapshot);

                                    setSourceContext(q => {
                                        return {
                                            ...q,
                                            json: result?.json,
                                            nonStandardSearchResult: null,
                                            scopeSearchResult: null,
                                            snapshot: snapshot,
                                        }
                                    });

                                } catch (error) {
                                    throw error;
                                } finally {
                                    setSourceContext(q => { return { ...q, searching: false } });
                                }

                            }
                        }}
                        onCancel={() => {
                            setSourceContext(q => {
                                return {
                                    json: null,
                                    nonStandardSearchResult: null,
                                    scopeSearchResult: null,
                                    snapshot: null
                                }
                            });
                        }}
                        searchOnMount={true}
                    />
                }

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null
                    }
                    label={'Entity'}
                    onSearch={onSearchScopes}
                    variant={"multiple"}
                    onPick={(picks) => {
                        const nonStandards: INonstandardScope[] = [];

                        // nothing to do
                        if (!picks || picks.length < 1) {
                            setSourceContext(q => {
                                return {
                                    json: null,
                                    nonStandardSearchResult: null,
                                    scopeSearchResult: null,
                                    snapshot: null
                                }
                            });
                            return;
                        }

                        // Group Configuration Scope:
                        // --------------------------------------
                        const gcNS = picks.find(q => sourceContext?.json === q.groupConfiguration);

                        if (gcNS && sourceContext?.json?.nonStandards)
                            sourceContext?.json?.nonStandards.forEach(t => {

                                if (t.deactivated)
                                    return;

                                nonStandards.push({
                                    type: ENonstandardScope.GroupConfiguration,
                                    parent: sourceContext.json!,
                                    nonStandard: t,
                                });
                            });

                        // Benefits Scope:
                        // --------------------------------------
                        sourceContext?.json?.benefits.forEach(t => {

                            if (t.deactivated)
                                return;

                            const picked = picks.find(q => q.benefit === t);

                            if (picked) {
                                t.nonStandards.forEach(q => {

                                    if (q.deactivated)
                                        return;

                                    nonStandards.push({
                                        type: ENonstandardScope.Benefit,
                                        parent: t,
                                        nonStandard: q,
                                    });
                                });
                            }

                        });

                        // Accounts Receivable Scope:
                        // --------------------------------------
                        sourceContext?.json?.claimsARs?.forEach(ar => {

                            if (ar.deactivated)
                                return;

                            const picked = picks.find(q => q.accountsReceivable === ar);

                            if (picked) {
                                ar.nonStandards?.forEach(q => {

                                    if (q.deactivated)
                                        return;

                                    nonStandards.push({
                                        type: ENonstandardScope.AccountsReceivable,
                                        parent: ar,
                                        nonStandard: q,
                                    });
                                });
                            }

                        });

                        // Fees and Premiums Scope:
                        // --------------------------------------
                        sourceContext?.json?.feesAndPremiums?.forEach(fp => {

                            if (fp.deactivated)
                                return;

                            const picked = picks.find(q => q.feesAndPremium === fp);

                            if (picked) {
                                fp.nonStandards?.forEach(q => {

                                    if (q.deactivated)
                                        return;

                                    nonStandards.push({
                                        type: ENonstandardScope.FeesAndPremiums,
                                        parent: fp,
                                        nonStandard: q,
                                    });
                                });
                            }

                        });

                        // Stop Loss Scope:
                        // --------------------------------------
                        sourceContext?.json?.stopLoss?.forEach(sl => {

                            if (sl.deactivated)
                                return;

                            const picked = picks.find(q => q.stopLoss === sl);

                            if (picked) {
                                sl.nonStandards?.forEach(q => {

                                    if (q.deactivated)
                                        return;

                                    nonStandards.push({
                                        type: ENonstandardScope.StopLoss,
                                        parent: sl,
                                        nonStandard: q,
                                    });
                                });
                            }

                        });

                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: nonStandards,
                                scopeSearchResult: picks,
                            }
                        });

                    }}
                    onCancel={() => {

                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}

                />

                <LookupControlDialog
                    isOpen={!!sourceContext?.nonStandardSearchResult}
                    label={'Non-Standards'}
                    variant="multiple"
                    onSearch={onSearchNonStandards}
                    onPick={(picks) => {

                        const nonStandards: FAM_INonStandard[] = [];

                        picks?.forEach(pick => {
                            const fieldMap = ArrayToLookupByPredicate(pick.item.nonStandard.fields, q => q.configRef.id);

                            // lets create a new nonstandard matching to current section
                            const template: FAM_INonStandard = createNonStandard(
                                props.state.data,
                                {
                                    section: props.section,
                                    benefit: props.benefit,
                                    groupConfiguration: props.groupConfiguration,
                                    accountsReceivable: props.accountsReceivable,
                                    feesAndPremium: props.feesAndPremium,
                                    stopLoss: props.stopLoss
                                }
                            );

                            let tierChanges: ITierLevelChange[] = [];
                            if (props.benefit)
                                if (pick.item.type === ENonstandardScope.Benefit)
                                    tierChanges = reconcileTiers(pick.item.parent.tiers, props.benefit?.tiers!, false);

                            // transfer matching properties of non/standard
                            template.name = `${pick.item.nonStandard.name} - imported - ${timestamp()}`;

                            template.fields.forEach(templateField => {

                                const field = fieldMap[templateField.configRef.id];

                                // did we find a matching field
                                if (field) {
                                    if (field.values.length === 0) {
                                        // nop
                                    }
                                    else if (field.values.length === 1 && templateField.values.length === 1) {

                                        const value = field.values[0].value;
                                        templateField.values[0].value = structuredClone(value);

                                    } else if (field.values.length > 1) {
                                        let predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                            return oldValue.type === newValue.type;
                                        }
                                        if (tierChanges.length > 0) {
                                            predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                                            };
                                        }

                                        templateField.values.forEach(t => {

                                            const value = field.values.find(q => predicate(q, t));

                                            if (value?.value) {
                                                t.value = structuredClone(value.value);
                                            }
                                        });
                                    }
                                }
                            });

                            nonStandards.push(template);
                        });


                        if (nonStandards && nonStandards.length > 0)
                            dispatch(
                                addUpdateNonStandards([
                                        {
                                            config: props.state.data.config,
                                            benefit: props.benefit,
                                            nonStandards: nonStandards,
                                            groupConfiguration: props.groupConfiguration,
                                            accountsReceivable: props.accountsReceivable,
                                            feesAndPremium: props.feesAndPremium,
                                            stopLoss: props.stopLoss,
                                            changeTracker: {}
                                        }
                                    ])
                            );

                        props.onDismiss?.();
                    }}
                    onCancel={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                />

            </Stack>
        </div>
    }

    function onRenderCopyFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
            />
            <DefaultButton text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => setView(EView.View_Source)}
            />
        </div>;
    }

    const onRenderImport = onRenderCopy;
    const onRenderImportFooter = onRenderCopyFooter;

    function onRenderUnknown(): JSX.Element {
        return <div>Unknown</div>
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.View_Source:
                onRender = onRenderEntry
                break;
            case EView.View_Add:
                onRender = onRenderAdd
                break;

            case EView.View_CreateTemplate:
                onRender = onRenderAdd
                break;

            case EView.View_Copy:
                onRender = onRenderCopy
                break;

            case EView.View_Import:
                onRender = onRenderImport
                break;

            default:
                onRender = onRenderUnknown
                break;
        }

        return <>
            <Separator />
            {
                validationMessage &&
                <>
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                </>
            }
            <div>
                {onRender()}
            </div>
        </>
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.View_Source:
                onRender = onRenderEntryFooter
                break;

            case EView.View_Add:
                onRender = onRenderAddFooter
                break;

            case EView.View_Copy:
                onRender = onRenderCopyFooter
                break;

            case EView.View_Import:
                onRender = onRenderImportFooter
                break;

            default:
                onRender = onRenderUnknown
                break;
        }

        return onRender();
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
        headerText="Non-Standard | Manage"
        isLightDismiss={true}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>;
}



function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardWizard);

